import React from 'react'
import { cn } from '../../utils/cn'

interface StepperStep {
  id: string
  title: string
  description?: string
  status: 'pending' | 'current' | 'completed'
}

interface StepperProps {
  steps: StepperStep[]
  orientation?: 'horizontal' | 'vertical'
  variant?: 'desktop' | 'mobile'
  className?: string
}

export const Stepper: React.FC<StepperProps> = ({
  steps,
  orientation = 'horizontal',
  variant = 'desktop',
  className,
}) => {
  const isDesktop = variant === 'desktop'
  const isMobile = variant === 'mobile'

  const getStepIcon = (step: StepperStep, index: number) => {
    if (step.status === 'completed') {
      return (
        <div className="w-10 h-10 bg-olr-complementary rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 " fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )
    }

    if (step.status === 'current') {
      return (
        <div className="w-10 h-10 bg-olr-complementary rounded-full flex items-center justify-center">
          <span className="text-olr-dark font-bold text-lg">{index + 1}</span>
        </div>
      )
    }

    return (
      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
        <span className="text-gray-600 font-bold text-lg">{index + 1}</span>
      </div>
    )
  }

  const getConnectorLine = (index: number, isLast: boolean) => {
    if (isLast) return null

    const isCompleted = steps[index].status === 'completed'
    const shouldHighlight = isCompleted

    if (isDesktop && orientation === 'horizontal') {
      return (
        <div className="flex-1 mx-6">
          <div
            className={cn(
              'h-1 transition-colors',
              shouldHighlight ? 'bg-olr-complementary' : 'bg-gray-300'
            )}
          />
        </div>
      )
    }

    if (orientation === 'vertical') {
      return (
        <div className="flex justify-center py-2">
          <div
            className={cn(
              'w-1 h-8 transition-colors',
              shouldHighlight ? 'bg-olr-complementary' : 'bg-gray-300'
            )}
          />
        </div>
      )
    }

    return null
  }

  if (isMobile) {
    return (
      <div
        className={cn('flex items-center justify-center space-x-3', className)}
      >
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {getStepIcon(step, index)}
            {index < steps.length - 1 && (
              <div className="flex-1 mx-3 max-w-12">
                <div
                  className={cn(
                    'h-1 transition-colors',
                    step.status === 'completed'
                      ? 'bg-olr-complementary'
                      : 'bg-gray-300'
                  )}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }

  if (orientation === 'horizontal') {
    return (
      <div className={cn('flex items-center w-full', className)}>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              {getStepIcon(step, index)}
              {step.title && (
                <div className="mt-2 text-center">
                  <div
                    className={cn(
                      'text-sm font-medium',
                      step.status === 'current'
                        ? 'text-blue-600'
                        : 'text-gray-600'
                    )}
                  >
                    {step.title}
                  </div>
                  {step.description && (
                    <div className="text-xs text-gray-500 mt-1">
                      {step.description}
                    </div>
                  )}
                </div>
              )}
            </div>
            {getConnectorLine(index, index === steps.length - 1)}
          </React.Fragment>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="flex items-start">
            {getStepIcon(step, index)}
            {step.title && (
              <div className="ml-4">
                <div
                  className={cn(
                    'text-sm font-medium',
                    step.status === 'current'
                      ? 'text-blue-600'
                      : 'text-gray-600'
                  )}
                >
                  {step.title}
                </div>
                {step.description && (
                  <div className="text-xs text-gray-500 mt-1">
                    {step.description}
                  </div>
                )}
              </div>
            )}
          </div>
          {getConnectorLine(index, index === steps.length - 1)}
        </React.Fragment>
      ))}
    </div>
  )
}

Stepper.displayName = 'Stepper'
